import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertTriangle,
  FileText,
  Plus,
  RefreshCw,
  Search,
  TrendingDown,
  Eye,
  Archive,
} from 'lucide-react';
import { barrattAPI, type SpendLeakageReport, type SpendLeakageDashboard } from '../lib/api';
import { format } from 'date-fns';
import { useFormatters } from '../contexts/PreferencesContext';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-500',
};

const SpendLeakage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<SpendLeakageReport[]>([]);
  const [dashboard, setDashboard] = useState<SpendLeakageDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [reportToPublish, setReportToPublish] = useState<SpendLeakageReport | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [newReport, setNewReport] = useState({
    title: '',
    description: '',
    period_start: '',
    period_end: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportsRes, dashboardRes] = await Promise.all([
        barrattAPI.getSpendLeakageReports(),
        barrattAPI.getSpendLeakageDashboard(),
      ]);

      setReports(reportsRes.data.results || []);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Failed to load spend leakage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    try {
      await barrattAPI.createSpendLeakageReport(newReport);
      setCreateDialogOpen(false);
      setNewReport({ title: '', description: '', period_start: '', period_end: '' });
      loadData();
    } catch (error) {
      console.error('Failed to create report:', error);
    }
  };

  const openPublishDialog = (report: SpendLeakageReport) => {
    setReportToPublish(report);
    setPublishDialogOpen(true);
  };

  const handlePublish = async () => {
    if (!reportToPublish) return;

    setPublishing(true);
    try {
      await barrattAPI.publishSpendLeakageReport(reportToPublish.id);
      setPublishDialogOpen(false);
      setReportToPublish(null);
      loadData();
    } catch (error) {
      console.error('Failed to publish report:', error);
    } finally {
      setPublishing(false);
    }
  };

  const filteredReports = reports.filter((report) => {
    // Filter by status
    if (statusFilter === 'active' && report.status === 'archived') return false;
    if (statusFilter === 'archived' && report.status !== 'archived') return false;

    // Filter by search term
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      report.report_number.toLowerCase().includes(search) ||
      report.title.toLowerCase().includes(search)
    );
  });

  const { formatCurrency: contextFormatCurrency } = useFormatters();

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return contextFormatCurrency(numAmount || 0);
  };

  const formatPercentage = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return (numValue || 0).toFixed(1);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Spend Leakage Analysis</h1>
          <p className="text-gray-500">Track and analyze off-contract and maverick spending</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Report
        </Button>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Total Leakage</p>
                <p className="text-3xl font-bold text-red-700">
                  {dashboard ? formatCurrency(dashboard.total_leakage) : '-'}
                </p>
              </div>
              <div className="p-3 bg-red-200 rounded-full">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Published Reports</p>
                <p className="text-3xl font-bold text-blue-700">
                  {dashboard?.report_count || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-200 rounded-full">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Action Required</p>
                <p className="text-3xl font-bold text-yellow-700">
                  {reports.filter((r) => r.status === 'draft').length}
                </p>
              </div>
              <div className="p-3 bg-yellow-200 rounded-full">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value: 'active' | 'archived' | 'all') => setStatusFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active Reports</SelectItem>
                <SelectItem value="archived">
                  <div className="flex items-center gap-2">
                    <Archive className="h-4 w-4" />
                    Archived
                  </div>
                </SelectItem>
                <SelectItem value="all">All Reports</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Spend Leakage Reports</CardTitle>
          <CardDescription>
            {filteredReports.length} reports found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No spend leakage reports found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report #</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Total Spend</TableHead>
                  <TableHead className="text-right">Leakage</TableHead>
                  <TableHead className="text-right">Leakage %</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="font-medium">
                      {report.report_number}
                    </TableCell>
                    <TableCell>{report.title}</TableCell>
                    <TableCell>
                      {format(new Date(report.period_start), 'dd MMM')} -{' '}
                      {format(new Date(report.period_end), 'dd MMM yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(report.total_spend)}
                    </TableCell>
                    <TableCell className="text-right text-red-600 font-medium">
                      {formatCurrency(report.leakage_spend)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={parseFloat(String(report.leakage_percentage)) > 10 ? 'text-red-600 font-bold' : ''}>
                        {formatPercentage(report.leakage_percentage)}%
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[report.status]}>
                        {report.status_display}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/spend-leakage/${report.id}`)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        {report.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => openPublishDialog(report)}
                          >
                            Publish
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Report Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Spend Leakage Report</DialogTitle>
            <DialogDescription>
              Create a new report to analyze off-contract spending
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Report Title *</Label>
              <Input
                value={newReport.title}
                onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                placeholder="e.g., Q4 2025 Spend Analysis"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newReport.description}
                onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                placeholder="Describe the scope of this report..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start *</Label>
                <Input
                  type="date"
                  value={newReport.period_start}
                  onChange={(e) => setNewReport({ ...newReport, period_start: e.target.value })}
                />
              </div>
              <div>
                <Label>Period End *</Label>
                <Input
                  type="date"
                  value={newReport.period_end}
                  onChange={(e) => setNewReport({ ...newReport, period_end: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateReport}
              disabled={!newReport.title || !newReport.period_start || !newReport.period_end}
            >
              Create Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Report</DialogTitle>
            <DialogDescription>
              Are you sure you want to publish this report? Once published, the report cannot be reverted to draft status.
            </DialogDescription>
          </DialogHeader>
          {reportToPublish && (
            <div className="py-4 space-y-2">
              <p className="font-medium">{reportToPublish.title}</p>
              <p className="text-sm text-gray-500">Report #{reportToPublish.report_number}</p>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">This action is irreversible</p>
                    <p>Publishing will finalize the report and make it available to all stakeholders.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPublishDialogOpen(false);
                setReportToPublish(null);
              }}
              disabled={publishing}
            >
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={publishing}>
              {publishing ? 'Publishing...' : 'Confirm Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpendLeakage;
